import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, Download, ArrowLeft, Save, Plus } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, BarChart, Bar, LabelList, Cell } from "recharts";
import InvestmentBreakdownPanel from "@/components/InvestmentBreakdownPanel";
import "./ProposalReport.css";

const rs = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const rs2 = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A4 Toyota-style Business Proposal + ROI report. */
export default function ProposalReport() {
    const { partId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [meta, setMeta] = useState({ end_application: "", sourcing: [], sop_schedule: [], yearly_volumes: [], prepared_by: "", approved_by: "", checked_by: "", report_version: "v1.0" });
    const [dirty, setDirty] = useState(false);
    const [recalcing, setRecalcing] = useState(false);

    // ?autoprint=1 → wait for data + a beat, then fire the browser print dialog.
    // Lets the costing page have a "Download PDF" button that one-clicks to here.
    useEffect(() => {
        if (!data) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("autoprint") === "1") {
            const t = setTimeout(() => { try { window.print(); } catch { /* silent */ } }, 800);
            return () => clearTimeout(t);
        }
    }, [data]);

    const load = async () => {
        try {
            const r = await api.get(`/parts/${partId}/report`);
            setData(r.data);
            const p = r.data.part || {};
            setMeta({
                end_application: p.end_application || "",
                sourcing: r.data.sourcing || [],
                sop_schedule: r.data.sop_schedule || [],
                yearly_volumes: p.yearly_volumes && p.yearly_volumes.length > 0
                    ? p.yearly_volumes
                    : (r.data.projection || []).map((py) => py.volume || 0),
                prepared_by: p.prepared_by || "",
                approved_by: p.approved_by || "",
                checked_by: p.checked_by || "",
                report_version: p.report_version || "v1.0",
            });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load report");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [partId]);

    const save = async () => {
        if (!data) return;
        const p = data.part;
        // Coerce SOP plan/actual weeks to numbers before persisting.
        const cleanSop = (meta.sop_schedule || []).map((s) => ({
            milestone: s.milestone || "",
            start_week: Number(s.start_week) || 0,
            end_week: Number(s.end_week) || 0,
            actual_start_week: Number(s.actual_start_week) || 0,
            actual_end_week: Number(s.actual_end_week) || 0,
        }));
        const cleanSourcing = (meta.sourcing || []).map((s) => ({
            ...s,
            price: Number(s.price) || 0,
        }));
        const cleanVols = (meta.yearly_volumes || []).map((v) => Number(v) || 0);
        try {
            setRecalcing(true);
            await api.put(`/parts/${partId}`, {
                ...p,
                end_application: meta.end_application,
                sourcing: cleanSourcing,
                sop_schedule: cleanSop,
                yearly_volumes: cleanVols,
                prepared_by: meta.prepared_by,
                approved_by: meta.approved_by,
                checked_by: meta.checked_by,
                report_version: meta.report_version,
            });
            await load();           // recalculates ROI + projection with new volumes
            setDirty(false);
            toast.success("Saved · figures recalculated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setRecalcing(false);
        }
    };

    const patchMeta = (patch) => { setMeta((m) => ({ ...m, ...patch })); setDirty(true); };
    const setSrc = (i, p) => patchMeta({ sourcing: meta.sourcing.map((s, idx) => idx === i ? { ...s, ...p } : s) });
    const addSrc = () => patchMeta({ sourcing: [...meta.sourcing, { vendor: "", item: "", price: 0 }] });
    const rmSrc = (i) => patchMeta({ sourcing: meta.sourcing.filter((_, idx) => idx !== i) });
    const setSop = (i, p) => patchMeta({ sop_schedule: meta.sop_schedule.map((s, idx) => idx === i ? { ...s, ...p } : s) });

    // Investment breakup (must be called before any conditional return)
    const invByCat = useMemo(() => {
        const draft = data?.draft;
        const map = { Machine: 0, Tool: 0, EF: 0 };
        (draft?.investments || []).forEach((i) => {
            const cat = (i.category || "").toLowerCase();
            const key = cat.includes("tool") ? "Tool" : (cat.includes("ef") || cat.includes("equip") || cat.includes("fix")) ? "EF" : "Machine";
            map[key] += Number(i.value) || 0;
        });
        const total = map.Machine + map.Tool + map.EF;
        return [
            { label: "M/c", value: map.Machine },
            { label: "Tool", value: map.Tool },
            { label: "EF", value: map.EF },
            { label: "Total", value: total },
        ];
    }, [data]);

    if (!data) return <div className="p-8 text-zinc-500 font-mono">Loading report…</div>;

    const { part, draft, totals, figures, roi } = data;
    const today = new Date();

    // Costing breakup data
    const costBars = [
        { label: "RM", value: totals.rm_total || 0, color: "#002FA7" },
        { label: "BOP", value: totals.bop_total || 0, color: "#5B6FE0" },
        ...(((totals.sub_assemblies_total || 0) > 0) ? [{ label: "Sub-Asm", value: totals.sub_assemblies_total || 0, color: "#7E57C2" }] : []),
        { label: "Process", value: totals.process_total || 0, color: "#0EA5E9" },
        { label: "Outsource", value: totals.outsource_total || 0, color: "#475569" },
        { label: "Investment", value: totals.amortised_investment_per_piece || 0, color: "#9C27B0" },
        { label: "Overhead", value: totals.overhead_fixed || 0, color: "#FFB703" },
        { label: "Profit", value: totals.profit_value || 0, color: "#2A9D8F" },
        { label: "Total", value: totals.final_cost || 0, color: "#10B981" },
    ];
    const totalCost = totals.final_cost || 1;

    // Proper waterfall data for cost breakup — each step stacks on running total
    const costWaterfall = (() => {
        const steps = costBars.filter((b) => b.label !== "Total" && b.value > 0);
        let running = 0;
        const out = steps.map((s) => {
            const item = { name: s.label, base: running, value: s.value, color: s.color };
            running += s.value;
            return item;
        });
        out.push({ name: "Total", base: 0, value: running, color: "#10B981", isTotal: true });
        return out;
    })();

    // Investment waterfall — each investment line stacks
    const investments = draft?.investments || [];
    const invWaterfall = (() => {
        let running = 0;
        const out = investments.filter((i) => Number(i.value) > 0).map((i, idx) => {
            const val = Number(i.value) || 0;
            const colors = ["#002FA7", "#5B6FE0", "#0EA5E9", "#F59E0B", "#2A9D8F", "#9C27B0"];
            const item = { name: i.name || `Inv ${idx + 1}`, base: running, value: val, color: colors[idx % colors.length] };
            running += val;
            return item;
        });
        if (out.length > 0) out.push({ name: "Total", base: 0, value: running, color: "#10B981", isTotal: true });
        return out;
    })();

    // Macho Value Addition = process + overhead_fixed + profit_value (also in totals.value_addition)
    const machoValueAddition = (totals.process_total || 0) + (totals.overhead_fixed || 0) + (totals.profit_value || 0);

    // Payback chart — uses projection (volume, revenue per year) + roi.cash_flow[i+1].cumulative
    // roi.cash_flow[0] is Year 0 (initial investment); subsequent entries correspond to project years 1..N
    const cfList = roi?.cash_flow || [];
    // Live projection — recompute revenue & cumulative from the user-edited yearly_volumes
    const derivedPrice = figures?.derived_price || 0;
    const costPerPiece = totals?.final_cost || 0;
    const investment = roi?.total_investment || 0;
    const annualVolDefault = Number(part.annual_volume || 0);
    const py = Number(part.project_years || 5) || 5;
    const liveVols = Array.from({ length: py }, (_, i) => {
        const v = Number((meta.yearly_volumes || [])[i]);
        return v > 0 ? v : annualVolDefault;
    });
    let runningCum = -investment;
    const yearsData = liveVols.map((vol, i) => {
        const revenue = vol * derivedPrice;
        const profit = vol * (derivedPrice - costPerPiece);
        runningCum += profit;
        return {
            year: `Y${i + 1}`,
            volume: vol,
            sales: revenue,
            profit,
            cum: runningCum,
        };
    });
    // Compute payback / ROI client-side from live data
    let livePayback = null;
    let prevCum = -investment;
    for (let i = 0; i < yearsData.length; i++) {
        const cum = yearsData[i].cum;
        if (cum >= 0 && prevCum < 0) {
            const fraction = (0 - prevCum) / (cum - prevCum);
            livePayback = i + fraction;
            break;
        }
        prevCum = cum;
    }
    const liveTotalProfit = yearsData.reduce((s, y) => s + y.profit, 0);
    const liveRoiPct = investment > 0 ? ((liveTotalProfit - investment) / investment) * 100 : 0;

    // Monthly cumulative cash flow — distribute each year's profit evenly across 12 months
    const monthsData = [];
    {
        let runningM = -investment;
        for (let yi = 0; yi < yearsData.length; yi++) {
            const monthlyProfit = (yearsData[yi].profit || 0) / 12;
            for (let m = 1; m <= 12; m++) {
                runningM += monthlyProfit;
                monthsData.push({
                    month: yi * 12 + m,
                    label: `M${yi * 12 + m}`,
                    cum: runningM,
                });
            }
        }
    }
    // Payback in months — extrapolate beyond project life if cumulative never crosses 0
    let livePaybackMonths = null;
    let paybackBeyondProject = false;
    {
        let prev = -investment;
        for (let i = 0; i < monthsData.length; i++) {
            const cur = monthsData[i].cum;
            if (cur >= 0 && prev < 0) {
                const frac = (0 - prev) / (cur - prev);
                livePaybackMonths = i + frac;
                break;
            }
            prev = cur;
        }
        // Extrapolation — use the LAST year's monthly profit to project payback beyond project life
        if (livePaybackMonths === null && investment > 0 && yearsData.length > 0) {
            const lastYearProfit = yearsData[yearsData.length - 1].profit || 0;
            const monthlyProfit = lastYearProfit / 12;
            const lastCum = monthsData.length > 0 ? monthsData[monthsData.length - 1].cum : -investment;
            if (monthlyProfit > 0 && lastCum < 0) {
                const extraMonths = (-lastCum) / monthlyProfit;
                livePaybackMonths = monthsData.length + extraMonths;
                paybackBeyondProject = true;
            }
        }
    }

    // BOM tree — hierarchical numbering (1 = FG, 1.1 = sub-assembly/child, 1.1.1 etc)
    const subAssemblies = totals.sub_assemblies || [];
    const bomItems = [];
    // Children first (1.1, 1.2 …) — each child gets its own indented number
    subAssemblies.forEach((sa, i) => {
        bomItems.push({
            num: `1.${i + 1}`,
            name: sa.name || sa.part_id,
            t: "Child Part",
            cost: sa.unit_cost,
            qty: sa.qty,
            indent: 1,
        });
    });
    // BOM components inside the selected draft (these belong to the FG itself)
    let cIdx = subAssemblies.length;
    (draft?.materials || []).forEach((m) => { cIdx += 1; bomItems.push({ num: `1.${cIdx}`, name: m.name || "RM", t: "RM", indent: 1 }); });
    (draft?.bop || []).forEach((b) => { cIdx += 1; bomItems.push({ num: `1.${cIdx}`, name: b.name || "BOP", t: "BOP", indent: 1 }); });
    (draft?.processes || []).forEach((p) => { cIdx += 1; bomItems.push({ num: `1.${cIdx}`, name: p.name || "Process", t: "Process", indent: 1 }); });
    (draft?.outsource || []).forEach((o) => { cIdx += 1; bomItems.push({ num: `1.${cIdx}`, name: o.process_name || o.name || "Outsource", t: "Outsource", indent: 1 }); });

    // Gantt total weeks
    const ganttMaxWk = Math.max(12, ...(meta.sop_schedule || []).map((s) => Number(s.end_week) || 0));

    return (
        <div>
            {/* Toolbar (hidden when printing) */}
            <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-3 flex items-center gap-3">
                <Button variant="outline" className="rounded-none" onClick={() => navigate(`/parts/${partId}/costing`)} data-testid="back-to-costing">
                    <ArrowLeft size={14} className="mr-2" /> Back
                </Button>
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">
                    Proposal Report · {part.part_name}
                </div>
                <div className="flex items-center gap-2 ml-4" data-testid="proposal-py-editor">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Project years</span>
                    <Input
                        type="number"
                        min="1"
                        max="20"
                        defaultValue={part.project_years || 5}
                        onBlur={async (e) => {
                            const v = Math.max(1, Math.min(20, Number(e.target.value) || 5));
                            if (v === (part.project_years || 5)) return;
                            try {
                                await api.put(`/parts/${partId}`, { ...part, project_years: v });
                                await load(); // re-fetch totals → ROI recalcs
                                toast.success(`Project life updated to ${v} yrs · ROI recalculated`);
                            } catch { toast.error("Couldn't update project years"); }
                        }}
                        className="h-7 w-16 rounded-none font-mono text-xs"
                        data-testid="proposal-py-input"
                    />
                </div>
                <div className="flex-1" />
                <Button variant="outline" className="rounded-none" onClick={save} disabled={!dirty} data-testid="save-report">
                    <Save size={14} className="mr-2" /> Save{dirty ? " *" : ""}
                </Button>
                <Button variant="outline" className="rounded-none" onClick={() => window.open(`${API}/parts/${partId}/proposal/export`, "_blank")} data-testid="excel-export">
                    <Download size={14} className="mr-2" /> Excel
                </Button>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B] text-white" onClick={() => window.print()} data-testid="print-report">
                    <Printer size={14} className="mr-2" /> Download PDF / Print
                </Button>
            </div>

            {/* A4 sheet */}
            <div className="bg-zinc-100 py-6 print:py-0 print:bg-white">
                <div className="report-sheet" data-testid="report-sheet">
                    <div className="r-stripe" />

                    {/* HEADER */}
                    <div className="flex items-start justify-between mb-4">
                        <img src="/macho-logo.png" alt="MACHO" style={{ height: "16mm" }} />
                        <div className="text-center">
                            <div className="r-h1">Business Proposal</div>
                            <div className="r-meta justify-center">
                                <span>Draft · {data.draft_name}</span>
                                <span>Status · {part.status}</span>
                            </div>
                        </div>
                        <div className="r-meta flex-col items-end" style={{ textAlign: "right" }}>
                            <div>Date: {today.toLocaleDateString("en-IN")}</div>
                            <div>Time: {today.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
                            <div>Version: {meta.report_version}</div>
                        </div>
                    </div>

                    {/* A) Business Details + Photo + End Application + BOM Tree */}
                    <div className="r-h2">A · Business Details</div>
                    <div className="r-grid r-grid-3a mb-4">
                        <table className="r-table" data-testid="report-business-table">
                            <tbody>
                                <tr><th>Customer</th><td>{part.customer_name || "—"}</td></tr>
                                <tr><th>Part Name</th><td>{part.part_name}</td></tr>
                                <tr><th>Part #</th><td>{part.part_number || "—"}</td></tr>
                                <tr><th>Volume / yr</th><td>{Number(part.annual_volume || 0).toLocaleString("en-IN")}</td></tr>
                                <tr><th>Expected Sales / yr</th><td>{rs(figures?.annual_revenue)}</td></tr>
                                <tr><th>Segment</th><td>{part.segment || "—"}</td></tr>
                                <tr><th>Target Price</th><td>{rs2(part.target_price)}</td></tr>
                                <tr><th>Derived Price</th><td>{rs2(figures?.derived_price)}</td></tr>
                            </tbody>
                        </table>
                        <div>
                            <div className="r-h3">Part Photo</div>
                            {part.photo_path ? (
                                <img className="r-photo" src={`${API}/files/${part.photo_path}`} alt={part.part_name} />
                            ) : (
                                <div className="r-photo flex items-center justify-center text-zinc-400 italic" style={{ display: "flex" }}>No photo</div>
                            )}
                            <div className="r-h3 mt-3 no-print">End Application</div>
                            <Input
                                className="rounded-none h-7 text-[8pt] no-print"
                                value={meta.end_application}
                                onChange={(e) => patchMeta({ end_application: e.target.value })}
                                placeholder="e.g. EV battery enclosure"
                                data-testid="report-end-application"
                            />
                        </div>
                        <div>
                            <div className="r-h3">BOM (Bill of Material)</div>
                            <div className="r-card bom-tree">
                                <div className="leaf head">1 · {part.part_name} {part.is_assembly ? "(Assembly · FG)" : "(Finished Good)"}</div>
                                <div className="branch">
                                    {bomItems.length === 0 && <div className="leaf italic text-zinc-400">No items</div>}
                                    {bomItems.map((b, i) => (
                                        <div className="leaf" key={i}>
                                            <span style={{ color: "#71717a" }}>{b.num}</span> <span style={{ color: "#71717a" }}>[{b.t}]</span> {b.name}
                                            {b.cost != null && <span className="text-[7pt] text-zinc-500"> · ₹{Number(b.cost).toFixed(2)}/pc × {b.qty}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* B) Costing Breakup waterfall + details table */}
                    <div className="r-h2">B · Costing Breakup (₹ per piece) — Waterfall</div>
                    <div className="r-grid r-grid-2">
                        <div style={{ height: "55mm" }} data-testid="report-costing-breakup">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={costWaterfall} margin={{ top: 14, right: 5, left: 0, bottom: 0 }}>
                                    <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 8 }} interval={0} />
                                    <YAxis tick={{ fontSize: 8 }} tickFormatter={(v) => `₹${v}`} width={40} />
                                    <Tooltip
                                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                        formatter={(v, n) => n === "base" ? null : [`₹${Number(v).toFixed(2)}`, "Adds"]}
                                        contentStyle={{ fontSize: 11 }}
                                    />
                                    <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
                                    <Bar dataKey="value" stackId="w" isAnimationActive={false}>
                                        {costWaterfall.map((d, i) => <Cell key={i} fill={d.color} />)}
                                        <LabelList dataKey="value" position="top" formatter={(v) => `₹${Number(v).toFixed(0)}`} style={{ fontSize: 8 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <table className="r-table" data-testid="report-costing-table">
                            <thead><tr><th>Section</th><th>Value (₹/pc)</th><th>% of Total</th></tr></thead>
                            <tbody>
                                {costBars.filter((b) => b.label !== "Total").map((b) => (
                                    <tr key={b.label}>
                                        <td><span style={{ display: "inline-block", width: "8px", height: "8px", background: b.color, marginRight: "5px" }} /> {b.label}</td>
                                        <td className="font-mono">{rs2(b.value).replace("₹ ", "")}</td>
                                        <td className="font-mono">{totalCost ? ((b.value / totalCost) * 100).toFixed(1) : "0.0"}%</td>
                                    </tr>
                                ))}
                                <tr className="tot">
                                    <td>Total / piece</td>
                                    <td className="font-mono">{rs2(totalCost).replace("₹ ", "")}</td>
                                    <td className="font-mono">100.0%</td>
                                </tr>
                                <tr style={{ background: "#fff7e6", fontWeight: 600 }}>
                                    <td>Macho Value Addition <span className="text-[7pt] text-zinc-500">(Process + Overhead + Profit)</span></td>
                                    <td className="font-mono" data-testid="report-macho-value-add">{rs2(machoValueAddition).replace("₹ ", "")}</td>
                                    <td className="font-mono">{totalCost ? ((machoValueAddition / totalCost) * 100).toFixed(1) : "0.0"}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* C) Investment + Payback */}
                    <div className="r-grid r-grid-2 mt-4">
                        <div>
                            <div className="r-h2">C · Investment — Waterfall</div>
                            {invWaterfall.length === 0 ? (
                                <div className="text-[8pt] italic text-zinc-400 py-6 text-center" data-testid="report-investment">No investment lines.</div>
                            ) : (
                                <div style={{ height: "50mm" }} data-testid="report-investment">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={invWaterfall} margin={{ top: 14, right: 5, left: 0, bottom: 0 }}>
                                            <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" tick={{ fontSize: 8 }} interval={0} />
                                            <YAxis tick={{ fontSize: 8 }} tickFormatter={(v) => `₹${(v / 1e5).toFixed(0)}L`} width={42} />
                                            <Tooltip
                                                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                                formatter={(v, n) => n === "base" ? null : [`₹${Number(v).toLocaleString("en-IN")}`, "Adds"]}
                                                contentStyle={{ fontSize: 11 }}
                                            />
                                            <Bar dataKey="base" stackId="i" fill="transparent" isAnimationActive={false} />
                                            <Bar dataKey="value" stackId="i" isAnimationActive={false}>
                                                {invWaterfall.map((d, i) => <Cell key={i} fill={d.color} />)}
                                                <LabelList dataKey="value" position="top" formatter={(v) => `₹${(v / 1e5).toFixed(1)}L`} style={{ fontSize: 8 }} />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            <div className="r-meta mt-2">
                                Amortisation: {draft?.amortise_investment ? "Amortised" : "Separate"} · Project life: {part.project_years} yrs · Total: <b>{rs(roi?.total_investment || 0)}</b>
                            </div>
                            {/* Audit-friendly roll-up of every investment across own + descendants */}
                            <div className="mt-3" data-testid="report-investment-breakdown">
                                <div className="r-h3 text-[9pt]">Investment breakdown — every line, with source</div>
                                <InvestmentBreakdownPanel partId={partId} compact testIdPrefix="report-inv-breakdown" />
                            </div>
                        </div>
                        <div>
                            <div className="r-h2">Projected Revenue + Pay-back</div>
                            <div style={{ height: "30mm" }} data-testid="report-revenue-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={yearsData} margin={{ top: 10, right: 5, left: 0, bottom: 0 }}>
                                        <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="year" tick={{ fontSize: 9 }} />
                                        <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `₹${(v / 1e5).toFixed(0)}L`} width={42} />
                                        <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} contentStyle={{ fontSize: 11 }} />
                                        <Bar dataKey="sales" fill="#002FA7" name="Revenue">
                                            <LabelList dataKey="sales" position="top" formatter={(v) => `₹${(v / 1e5).toFixed(1)}L`} style={{ fontSize: 8 }} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div style={{ height: "30mm" }} data-testid="report-payback-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={monthsData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#2A9D8F" stopOpacity={0.5} />
                                                <stop offset="100%" stopColor="#2A9D8F" stopOpacity={0.05} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="month" tick={{ fontSize: 9 }} tickFormatter={(v) => `M${v}`} interval={Math.max(2, Math.floor(monthsData.length / 8))} />
                                        <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `₹${(v / 1e5).toFixed(0)}L`} width={42} />
                                        <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} labelFormatter={(l) => `Month ${l}`} contentStyle={{ fontSize: 11 }} />
                                        <ReferenceLine y={0} stroke="#09090b" strokeWidth={1} />
                                        {roi?.payback_months != null && roi.payback_months > 0 && roi.payback_months <= monthsData.length && (
                                            <ReferenceLine
                                                x={Math.round(roi.payback_months)}
                                                stroke="#E63946"
                                                strokeDasharray="3 3"
                                                strokeWidth={1.5}
                                                label={{ value: `Payback M${Math.round(roi.payback_months)}`, position: "top", fill: "#E63946", fontSize: 9 }}
                                            />
                                        )}
                                        <Area type="monotone" dataKey="cum" stroke="#002FA7" strokeWidth={2} fill="url(#cumGrad)" name="Cumulative Cash Flow" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                            <table className="r-table mt-2" data-testid="report-payback">
                                <thead><tr><th>Year</th><th>Volume {dirty && <span className="text-[#E63946] no-print" title="Unsaved changes">*</span>}</th><th>Sales (₹)</th><th>Cumulative (₹)</th></tr></thead>
                                <tbody>
                                    {yearsData.map((y, i) => (
                                        <tr key={i}>
                                            <td>{y.year}</td>
                                            <td>
                                                <Input
                                                    type="number"
                                                    className="rounded-none h-6 text-[8pt] border-0 p-0 font-mono w-full"
                                                    value={(meta.yearly_volumes && meta.yearly_volumes[i] !== undefined) ? meta.yearly_volumes[i] : y.volume}
                                                    onChange={(e) => {
                                                        const next = [...(meta.yearly_volumes || [])];
                                                        while (next.length < liveVols.length) next.push(annualVolDefault);
                                                        next[i] = e.target.value;
                                                        patchMeta({ yearly_volumes: next });
                                                    }}
                                                    data-testid={`yearly-vol-${i}`}
                                                />
                                            </td>
                                            <td>{rs(y.sales)}</td>
                                            <td style={{ color: y.cum >= 0 ? "#2A9D8F" : "#E63946", fontWeight: 600 }}>{rs(y.cum)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="r-meta mt-2 flex items-center justify-between">
                                <span>
                                    Payback: <b>{roi?.payback_months != null ? `${Number(roi.payback_months).toFixed(1)} months` : "Investment not recoverable (annual cash flow ≤ 0)"}</b>
                                    {" · "}ROI: <b>{roi?.roi_pct != null ? `${Number(roi.roi_pct).toFixed(1)}%` : "—"}</b>
                                    {" · "}Net Profit: <b>{rs(roi?.net_profit || 0)}</b>
                                    {" · "}Annual Cash Flow: <b>{rs(roi?.annual_net_cash_flow || 0)}</b>
                                </span>
                                <span className="no-print text-[7pt] text-zinc-400 italic">
                                    {dirty ? "* edit volumes then click Save to persist" : recalcing ? "Recalculating…" : "Up to date"}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* D) Sourcing Details */}
                    <div className="r-h2 mt-4">D · Sourcing Details</div>
                    <table className="r-table" data-testid="report-sourcing">
                        <thead><tr><th style={{ width: "30%" }}>Item</th><th style={{ width: "30%" }}>Vendor</th><th style={{ width: "15%" }}>Price (₹)</th><th>Notes</th><th className="no-print" style={{ width: "10mm" }}></th></tr></thead>
                        <tbody>
                            {meta.sourcing.length === 0 && <tr><td colSpan={5} className="italic text-zinc-400 text-center">No sourcing entries — add below.</td></tr>}
                            {meta.sourcing.map((s, i) => (
                                <tr key={i}>
                                    <td><Input className="rounded-none h-6 text-[8pt] border-0 p-0" value={s.item || ""} onChange={(e) => setSrc(i, { item: e.target.value })} /></td>
                                    <td><Input className="rounded-none h-6 text-[8pt] border-0 p-0" value={s.vendor || ""} onChange={(e) => setSrc(i, { vendor: e.target.value })} /></td>
                                    <td><Input type="number" className="rounded-none h-6 text-[8pt] border-0 p-0 font-mono" value={s.price ?? 0} onChange={(e) => setSrc(i, { price: e.target.value })} /></td>
                                    <td><Input className="rounded-none h-6 text-[8pt] border-0 p-0" value={s.notes || ""} onChange={(e) => setSrc(i, { notes: e.target.value })} /></td>
                                    <td className="no-print text-center"><button onClick={() => rmSrc(i)} className="text-zinc-400 hover:text-[#E63946] text-xs">✕</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="no-print mt-1">
                        <button onClick={addSrc} className="text-[8pt] uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="report-add-sourcing">+ Add sourcing row</button>
                    </div>

                    {/* E) SOP Gantt — starts on a new A4 page when printing */}
                    <div className="r-h2 mt-4 report-page-break flex items-center justify-between">
                        <span>E · SOP (Start of Production) Schedule — Plan vs Actual</span>
                        <button onClick={() => patchMeta({ sop_schedule: [...meta.sop_schedule, { milestone: "New milestone", start_week: 0, end_week: 2, actual_start_week: 0, actual_end_week: 0 }] })} className="no-print text-[10px] uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="report-add-sop">
                            + Add row
                        </button>
                    </div>
                    <div data-testid="report-sop-schedule">
                        <div className="gantt-row text-zinc-500" style={{ borderBottom: "1px solid #d4d4d8" }}>
                            <div className="text-[7pt] uppercase tracking-wider">Milestone</div>
                            <div className="text-[7pt] uppercase tracking-wider">Plan (blue) vs Actual (red) — {ganttMaxWk} weeks</div>
                        </div>
                        {meta.sop_schedule.map((s, i) => {
                            const pStart = Number(s.start_week) || 0;
                            const pEnd = Number(s.end_week) || 0;
                            const aStart = Number(s.actual_start_week) || 0;
                            const aEnd = Number(s.actual_end_week) || 0;
                            const planL = pStart / ganttMaxWk * 100;
                            const planW = Math.max(1, (pEnd - pStart) / ganttMaxWk * 100);
                            const actL = aStart / ganttMaxWk * 100;
                            const actW = aEnd > aStart ? (aEnd - aStart) / ganttMaxWk * 100 : 0;
                            return (
                                <div className="gantt-row" key={i}>
                                    <div className="flex items-center gap-1">
                                        <Input
                                            className="rounded-none h-6 text-[8pt] border-0 p-0 flex-1"
                                            value={s.milestone || ""}
                                            onChange={(e) => setSop(i, { milestone: e.target.value })}
                                            data-testid={`sop-name-${i}`}
                                        />
                                        <button onClick={() => patchMeta({ sop_schedule: meta.sop_schedule.filter((_, idx) => idx !== i) })} className="no-print text-zinc-400 hover:text-[#E63946] text-[10px]" title="Remove">✕</button>
                                    </div>
                                    <div>
                                        <div className="gantt-track">
                                            {/* Plan bar */}
                                            <div className="gantt-bar" style={{ left: `${planL}%`, width: `${planW}%`, top: 0, bottom: "50%", background: "#002FA7" }} title={`Plan: W${pStart}–W${pEnd}`} />
                                            {/* Actual bar */}
                                            {actW > 0 && (
                                                <div className="gantt-bar" style={{ left: `${actL}%`, width: `${actW}%`, top: "50%", bottom: 0, background: "#E63946" }} title={`Actual: W${aStart}–W${aEnd}`} />
                                            )}
                                            <div className="gantt-week" style={{ left: `${planL}%` }}>W{pStart}</div>
                                            <div className="gantt-week" style={{ left: `${planL + planW}%` }}>W{pEnd}</div>
                                        </div>
                                        <div className="no-print flex items-center gap-1 mt-1 text-[7pt] text-zinc-500 font-mono">
                                            <span style={{ width: "28px" }}>Plan:</span>
                                            <Input type="number" className="rounded-none h-5 w-12 text-[8pt] font-mono p-1" value={pStart} onChange={(e) => setSop(i, { start_week: e.target.value })} data-testid={`sop-plan-start-${i}`} />
                                            <span>→</span>
                                            <Input type="number" className="rounded-none h-5 w-12 text-[8pt] font-mono p-1" value={pEnd} onChange={(e) => setSop(i, { end_week: e.target.value })} data-testid={`sop-plan-end-${i}`} />
                                            <span style={{ width: "40px", marginLeft: "8px" }}>Actual:</span>
                                            <Input type="number" className="rounded-none h-5 w-12 text-[8pt] font-mono p-1" value={aStart} onChange={(e) => setSop(i, { actual_start_week: e.target.value })} data-testid={`sop-actual-start-${i}`} />
                                            <span>→</span>
                                            <Input type="number" className="rounded-none h-5 w-12 text-[8pt] font-mono p-1" value={aEnd} onChange={(e) => setSop(i, { actual_end_week: e.target.value })} data-testid={`sop-actual-end-${i}`} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="r-meta mt-1" style={{ fontSize: "7pt" }}>
                        <span style={{ display: "inline-block", width: "10px", height: "6px", background: "#002FA7", marginRight: "4px" }} /> Plan
                        <span style={{ display: "inline-block", width: "10px", height: "6px", background: "#E63946", marginLeft: "12px", marginRight: "4px" }} /> Actual
                    </div>

                    {/* Footer signatures */}
                    <div className="r-footer">
                        <div>
                            <div className="r-h3">Prepared by</div>
                            <span className="sig-line" />
                            <Input className="rounded-none h-6 text-[8pt] border-0 p-0" value={meta.prepared_by} onChange={(e) => patchMeta({ prepared_by: e.target.value })} placeholder="Name" data-testid="report-prepared-by" />
                        </div>
                        <div>
                            <div className="r-h3">Approved by</div>
                            <span className="sig-line" />
                            <Input className="rounded-none h-6 text-[8pt] border-0 p-0" value={meta.approved_by} onChange={(e) => patchMeta({ approved_by: e.target.value })} placeholder="Name" data-testid="report-approved-by" />
                        </div>
                        <div>
                            <div className="r-h3">Checked by</div>
                            <span className="sig-line" />
                            <Input className="rounded-none h-6 text-[8pt] border-0 p-0" value={meta.checked_by} onChange={(e) => patchMeta({ checked_by: e.target.value })} placeholder="Name" data-testid="report-checked-by" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
